import type { Geometry } from './types';

/** 默认网格尺寸（毫米）。M3b 只做开关，不做可配置格距，保持范围可控。 */
export const GRID_SIZE_MM = 50;

export function snapToGrid(value: number, gridSize: number = GRID_SIZE_MM): number {
  return Math.round(value / gridSize) * gridSize;
}

export interface AlignmentGuide {
  readonly orientation: 'v' | 'h';
  /** 逻辑毫米坐标 */
  readonly pos: number;
}

export interface AlignmentResult {
  readonly x: number;
  readonly y: number;
  readonly guides: readonly AlignmentGuide[];
}

/**
 * 参考线吸附（架构书 §10.2）：把被拖动矩形的左/右/中/上/下/中 六条边分别与
 * 场景内其他对象的同类边比较，落在阈值内就吸附到那条边并返回参考线用于渲染。
 * 网格吸附开启时不叠加这个（见调用方 `layout-canvas.tsx` 里的互斥逻辑），
 * 避免两套吸附互相拉扯出用户看不懂的抖动。
 */
export function computeAlignmentSnap(
  dragged: Pick<Geometry, 'x' | 'y' | 'w' | 'h'>,
  others: ReadonlyArray<Pick<Geometry, 'x' | 'y' | 'w' | 'h'>>,
  thresholdMm: number,
): AlignmentResult {
  const dLeft = dragged.x;
  const dRight = dragged.x + dragged.w;
  const dCenterX = dragged.x + dragged.w / 2;
  const dTop = dragged.y;
  const dBottom = dragged.y + dragged.h;
  const dCenterY = dragged.y + dragged.h / 2;

  let bestDx: number | null = null;
  let bestDxDist = Infinity;
  let bestDy: number | null = null;
  let bestDyDist = Infinity;
  let guideV: number | null = null;
  let guideH: number | null = null;

  for (const o of others) {
    const oLeft = o.x;
    const oRight = o.x + o.w;
    const oCenterX = o.x + o.w / 2;
    const oTop = o.y;
    const oBottom = o.y + o.h;
    const oCenterY = o.y + o.h / 2;

    for (const [dEdge, target] of [
      [dLeft, oLeft],
      [dLeft, oRight],
      [dRight, oLeft],
      [dRight, oRight],
      [dCenterX, oCenterX],
    ] as const) {
      const dist = Math.abs(dEdge - target);
      if (dist <= thresholdMm && dist < bestDxDist) {
        bestDxDist = dist;
        bestDx = target - dEdge;
        guideV = target;
      }
    }

    for (const [dEdge, target] of [
      [dTop, oTop],
      [dTop, oBottom],
      [dBottom, oTop],
      [dBottom, oBottom],
      [dCenterY, oCenterY],
    ] as const) {
      const dist = Math.abs(dEdge - target);
      if (dist <= thresholdMm && dist < bestDyDist) {
        bestDyDist = dist;
        bestDy = target - dEdge;
        guideH = target;
      }
    }
  }

  const guides: AlignmentGuide[] = [];
  if (guideV !== null) guides.push({ orientation: 'v', pos: guideV });
  if (guideH !== null) guides.push({ orientation: 'h', pos: guideH });

  return {
    x: Math.round(dragged.x + (bestDx ?? 0)),
    y: Math.round(dragged.y + (bestDy ?? 0)),
    guides,
  };
}

/**
 * 等距分布（roadmap M3b）：两端对象位置不动，中间对象在同一根轴上重新摆放，
 * 使相邻对象之间的**间隙**相等。至少需要 3 个对象才有意义。
 */
export function distributeEqually<
  T extends { readonly id: string } & Pick<Geometry, 'x' | 'y' | 'w' | 'h'>,
>(items: readonly T[], axis: 'x' | 'y'): ReadonlyMap<string, number> {
  const result = new Map<string, number>();
  if (items.length < 3) return result;

  const dim = axis === 'x' ? 'w' : 'h';
  const sorted = [...items].sort((a, b) => a[axis] - b[axis]);
  const first = sorted[0]!;
  const last = sorted.at(-1)!;

  const span = last[axis] + last[dim] - first[axis];
  const totalSize = sorted.reduce((sum, o) => sum + o[dim], 0);
  const gap = (span - totalSize) / (sorted.length - 1);

  // 累加用未取整的精确值，只在写回结果时取整一次——否则逐项取整的误差会
  // 越靠后越大，"等距"看起来就不等了。
  let cursor = first[axis] + first[dim];
  for (let i = 1; i < sorted.length - 1; i++) {
    const item = sorted[i]!;
    cursor += gap;
    result.set(item.id, Math.round(cursor));
    cursor += item[dim];
  }
  return result;
}
